import { memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";

import { Box, Divider, Tooltip, Typography, useTheme } from "@mui/material";

const FGSellerInfoTooltip = ({ sellerInfo, children }) => {
  const theme = useTheme();

  const renderSection = useCallback(
    (title, rows) => (
      <Box flex={1} minWidth={300}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          {title}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {rows.map((row) => (
          <Box key={row.label} display="flex" alignItems="flex-start" mb={1.5}>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, minWidth: 160, pr: 2 }}
            >
              {row.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 700,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                flex: 1,
              }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    ),
    [theme],
  );

  const sourceRows = useMemo(
    () => [
      {
        label: "Name",
        value: sellerInfo?.sourceSeller?.sellerDisplayName || "-",
      },
      { label: "Mobile no.", value: sellerInfo?.sourceSeller?.mobile || "-" },
      { label: "Email ID", value: sellerInfo?.sourceSeller?.email || "-" },
      { label: "GSTIN", value: sellerInfo?.sourceSeller?.gstin || "-" },
      { label: "Ship from", value: sellerInfo?.sourceSeller?.shipFrom || "-" },
      { label: "Bill from", value: sellerInfo?.sourceSeller?.billFrom || "-" },
      {
        label: "Seller Display name",
        value: sellerInfo?.sourceSeller?.name || "-",
      },
    ],
    [sellerInfo?.sourceSeller],
  );

  const jodlRows = useMemo(
    () => [
      { label: "Name", value: sellerInfo?.jodl?.name || "-" },
      { label: "GSTIN", value: sellerInfo?.jodl?.gstin || "-" },
    ],
    [sellerInfo?.jodl],
  );

  return (
    <Tooltip
      arrow
      placement="bottom-start"
      enterDelay={120}
      title={
        <Box
          sx={{
            width: 720,
            maxWidth: "calc(100vw - 64px)",
            p: 2,
            display: "flex",
            gap: 2,
          }}
        >
          {renderSection("Source seller", sourceRows)}
          <Divider orientation="vertical" flexItem />
          {renderSection("JODL", jodlRows)}
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: "none",
            bgcolor: theme.palette.grey[100],
            color: theme.palette.text.primary,
            borderRadius: 2,
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 4,
            p: 0,
            overflow: "hidden",
          },
        },
        arrow: {
          sx: {
            color: theme.palette.grey[100],
          },
        },
      }}
    >
      <Box component="span">{children}</Box>
    </Tooltip>
  );
};

FGSellerInfoTooltip.propTypes = {
  sellerInfo: PropTypes.shape({
    sourceSeller: PropTypes.shape({
      name: PropTypes.string,
      mobile: PropTypes.string,
      email: PropTypes.string,
      gstin: PropTypes.string,
      shipFrom: PropTypes.string,
      billFrom: PropTypes.string,
      sellerDisplayName: PropTypes.string,
    }),
    jodl: PropTypes.shape({
      name: PropTypes.string,
      gstin: PropTypes.string,
    }),
  }).isRequired,
  children: PropTypes.node.isRequired,
};

export default memo(FGSellerInfoTooltip);
